import type { Tables } from '@hive/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { deviceName, subscribeThisDevice, thisDeviceEndpoint, unsubscribeThisDevice } from '@/lib/push';
import { supabase } from '@/lib/supabase';

export type PushDevice = Pick<Tables<'push_subscriptions'>, 'id' | 'endpoint' | 'device_name' | 'created_at'>;

const DEVICES_KEY = ['push-devices'] as const;
const THIS_DEVICE_KEY = ['push-this-device'] as const;

/** Your devices with phone/browser notifications on (RLS shows only yours). */
export function useMyDevices() {
  return useQuery({
    queryKey: DEVICES_KEY,
    queryFn: async (): Promise<PushDevice[]> => {
      const { data, error } = await supabase()
        .from('push_subscriptions')
        .select('id, endpoint, device_name, created_at')
        .order('created_at');
      if (error) throw error;
      return data;
    },
  });
}

/** This browser's push address, if it's subscribed. */
export function useThisDeviceEndpoint() {
  return useQuery({ queryKey: THIS_DEVICE_KEY, queryFn: thisDeviceEndpoint, staleTime: Infinity });
}

/** Turns notifications on for this device: permission, subscription, and a row so the push function can find it. */
export function useNotifyThisDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vapidKey: string) => {
      const keys = await subscribeThisDevice(vapidKey);
      const client = supabase();
      // A device subscribed before keeps its row; re-subscribing refreshes its keys.
      const { data: existing, error: findError } = await client
        .from('push_subscriptions')
        .select('id')
        .eq('endpoint', keys.endpoint)
        .maybeSingle();
      if (findError) throw findError;
      const fields = { p256dh: keys.p256dh, auth: keys.auth, device_name: deviceName(navigator.userAgent) };
      const { error } = existing
        ? await client.from('push_subscriptions').update(fields).eq('id', existing.id)
        : await client.from('push_subscriptions').insert({ endpoint: keys.endpoint, ...fields });
      if (error) throw error;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: DEVICES_KEY });
      void queryClient.invalidateQueries({ queryKey: THIS_DEVICE_KEY });
    },
  });
}

/** Stops notifications to a device; for this device, the browser's subscription goes too. */
export function useRemoveDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (device: PushDevice) => {
      const { error } = await supabase().from('push_subscriptions').delete().eq('id', device.id);
      if (error) throw error;
      if (device.endpoint === (await thisDeviceEndpoint())) await unsubscribeThisDevice();
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: DEVICES_KEY });
      void queryClient.invalidateQueries({ queryKey: THIS_DEVICE_KEY });
    },
  });
}

const HEARTBEAT_MS = 60_000;

/**
 * While Hive is open and visible here, tells the push function so every minute:
 * an open app rings its own notifications, so this device needn't get a push too.
 */
export function useDeviceHeartbeat(endpoint: string | null | undefined) {
  useEffect(() => {
    if (!endpoint) return;
    const beat = () => {
      if (document.visibilityState !== 'visible') return;
      void supabase()
        .from('push_subscriptions')
        .update({ active_at: new Date().toISOString() })
        .eq('endpoint', endpoint)
        .then(() => undefined);
    };
    beat();
    const timer = window.setInterval(beat, HEARTBEAT_MS);
    document.addEventListener('visibilitychange', beat);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', beat);
    };
  }, [endpoint]);
}
